import { useDispatch, useSelector, useStore } from 'react-redux';
import {
    addExperience,
    resetExperience,
    addSharingStation,
    removeSharingStation,
    updateSharingStation,
    setMirror,
} from '../utils/state/experienceSlice';
import {
    addScreen,
    resetScreens,
    updateScreen,
} from '../utils/state/screensSlice';
import {
    clearAllFlows,
    addProcessingFlowFromDefinition,
} from '../utils/state/processingFlowSlice';
import { useEffect } from 'react';

// Helper function to sanitize element metadata for export
const sanitizeElementMetadata = (element: UIElement): any => {
    const metadata: any = { ...element.metadata };

    // Fix selectedQR, currentSessionQR, paymentQR - ensure width/height are present
    if (
        element.type === 'selectedQR' ||
        element.type === 'currentSessionQR' ||
        element.type === 'paymentQR'
    ) {
        if (!metadata.width || metadata.width === 0) {
            metadata.width = 200;
        }
        if (!metadata.height || metadata.height === 0) {
            metadata.height = 200;
        }
    }

    return metadata;
};

// Helper function to transform element properties for export
const transformElementForExport = (element: UIElement): any => {
    const { conditionalRendering, ...rest } = element;
    const transformedElement: any = {
        ...rest,
        metadata: sanitizeElementMetadata(element),
    };

    // Transform conditionalRendering to conditional
    if (conditionalRendering) {
        transformedElement.conditional = conditionalRendering;
    }

    return transformedElement;
};

// Helper function to sanitize capture metadata
const sanitizeCaptureMetadata = (metadata: any): any => {
    const sanitized = { ...metadata };

    // Ensure shouldNavigateAfterCapture is always a boolean (required field)
    if (typeof sanitized.shouldNavigateAfterCapture !== 'boolean') {
        sanitized.shouldNavigateAfterCapture = false;
    }

    // Ensure prompt is always a string (required field)
    if (typeof sanitized.prompt !== 'string') {
        sanitized.prompt = '';
    }

    // Fix videoProcessingFlowId - must be undefined or number, never empty string
    if (
        sanitized.videoProcessingFlowId === '' ||
        sanitized.videoProcessingFlowId === null
    ) {
        delete sanitized.videoProcessingFlowId;
    } else if (sanitized.videoProcessingFlowId !== undefined) {
        // Ensure it's a number if present
        const parsed = parseInt(sanitized.videoProcessingFlowId);
        if (!isNaN(parsed)) {
            sanitized.videoProcessingFlowId = parsed;
        } else {
            delete sanitized.videoProcessingFlowId;
        }
    }

    return sanitized;
};

// Helper function to sanitize screen stage metadata
const sanitizeStageMetadata = (stage: any): any => {
    if (stage.type === 'capture') {
        return {
            ...stage,
            metadata: sanitizeCaptureMetadata(stage.metadata || {}),
        };
    }
    return stage;
};

// Helper function to transform screen for export
const transformScreenForExport = (screen: ScreenDefinition): any => {
    const transformedScreen = {
        ...screen,
        stage: sanitizeStageMetadata(screen.stage),
        elements: screen.elements.map(transformElementForExport),
    };
    return transformedScreen;
};

// Helper function to transform element properties for import
const transformElementForImport = (element: any): UIElement => {
    const { conditional, ...rest } = element;
    const transformedElement: any = { ...rest };

    // Transform conditional back to conditionalRendering
    if (conditional) {
        transformedElement.conditionalRendering = conditional;
    }

    return transformedElement as UIElement;
};

// Helper function to transform screen for import
const transformScreenForImport = (screen: any): ScreenDefinition => {
    const transformedScreen = {
        ...screen,
        elements: screen.elements.map(transformElementForImport),
    };
    return transformedScreen as ScreenDefinition;
};

export default function useExperienceState(activationId: number) {
    const dispatch = useDispatch();

    // Get only main experience screens (not sharing station screens)
    const screens = useSelector((state: any) => {
        try {
            if (!state?.screens?.present?.ids || !activationId) {
                return [];
            }

            // Get the main experience screen IDs
            const experience =
                state?.experiences?.present?.entities?.[activationId];
            const mainScreenIds = experience?.screens || [];

            // Return only screens that belong to the main experience
            const result = mainScreenIds
                .map((id: string) => state.screens.present.entities[id])
                .filter(Boolean); // Filter out any undefined entries

            return result;
        } catch (error) {
            console.error('Error in screens selector:', error);
            return [];
        }
    });

    // Get all screens (including sharing station screens) for resolving screen IDs
    const allScreens = useSelector((state: any) => {
        try {
            if (!state?.screens?.present?.ids) {
                return [];
            }
            const result = state.screens.present.ids
                .map((id: string) => state.screens.present.entities[id])
                .filter(Boolean); // Filter out any undefined entries

            return result;
        } catch (error) {
            console.error('Error in allScreens selector:', error);
            return [];
        }
    });

    // Use a simpler approach for sharing - just return empty array if anything fails
    const sharing = useSelector((state: any) => {
        try {
            // If no valid activationId, return empty array
            if (!activationId || activationId === 0) {
                return [];
            }

            // Navigate safely through the state structure
            const experience =
                state?.experiences?.present?.entities?.[activationId];
            return experience?.sharing || [];
        } catch (error) {
            console.error('Error in sharing selector:', error);
            return [];
        }
    });

    // Mirror-booth flag: absent means OFF (matches the booth's default).
    const mirrorEnabled = useSelector((state: any) => {
        const experience =
            state?.experiences?.present?.entities?.[activationId];
        return experience?.mirror ?? false;
    });

    const setMirrorEnabled = (mirror: boolean) => {
        dispatch(
            setMirror({
                experienceId: activationId,
                mirror,
            }),
        );
    };

    const store = useStore();

    const loadExperience = (definition: Definition, activationId: number) => {
        dispatch(resetExperience());
        dispatch(resetScreens());
        dispatch(clearAllFlows());

        const flows = definition.processingFlows as Record<
            string,
            ProcessingFlow
        >;

        let experience = {
            id: activationId,
            screens: definition.screens.map((screen) => screen.id),
            processingFlows: Object.keys(flows),
            exitPin: definition.exitPin,
            mirror: definition.mirror,
            sharing: definition.sharing?.map((sharing) => ({
                name: sharing.name,
                screens: sharing.screens.map((screen) => screen.id),
            })),
        };

        let sharingScreens: ScreenDefinition[] = [];

        if (definition.sharing && definition.sharing?.length > 0) {
            definition.sharing.forEach((sharing) => {
                sharingScreens.push(...sharing.screens);
            });
        }

        let screens = definition.screens.map(transformScreenForImport);

        dispatch(addExperience(experience));

        sharingScreens.map(transformScreenForImport).forEach((screen) => {
            dispatch(addScreen(screen));
        });

        screens.forEach((screen) => {
            dispatch(addScreen(screen));
        });

        if (flows && typeof flows === 'object') {
            Object.keys(flows).forEach((flowId: string) => {
                const flow = flows[flowId];
                dispatch(addProcessingFlowFromDefinition({ id: flowId, flow }));
            });
        }
    };

    const handleChange = (
        screen: ScreenDefinition,
        name: string,
        value: string,
    ) => {
        let newData = { [name]: value };

        dispatch(updateScreen({ id: screen.id, data: newData }));
    };

    const handleSwitchChange = (
        screen: ScreenDefinition,
        name: string,
        value: boolean,
    ) => {
        let newData = { [name]: value };

        dispatch(updateScreen({ id: screen.id, data: newData }));
    };

    const handleSelectChange = (
        screen: ScreenDefinition,
        name: string,
        value: string,
    ) => {
        let newData = { [name]: value };

        dispatch(updateScreen({ id: screen.id, data: newData }));
    };

    const exportExperience = (activationId: number): Definition => {
        const storeState: any = store.getState();
        const localExperience =
            storeState.experiences.present.entities[activationId];
        const localScreens = storeState.screens.present.entities;

        if (!localExperience) {
            return {
                activationId,
                version: 1,
                definitionVersion: 1,
                screens: [],
                processingFlows: {},
                exitPin: undefined,
                mirror: undefined,
                sharing: [],
            };
        }

        const screenIds = localExperience.screens;

        const screens: ScreenDefinition[] = [];
        screenIds.forEach((screenId: ScreenId) => {
            const screen = localScreens[screenId];
            if (screen) {
                // Transform the screen for export (conditionalRendering -> conditional)
                const transformedScreen = transformScreenForExport(screen);
                screens.push(transformedScreen);
            }
        });

        // Handle both legacy and visual processing flows
        const flows: Record<string, ProcessingFlow> = {};

        // Export legacy flows that were loaded
        const flowsState = storeState.flows.present;
        if (flowsState && flowsState.legacyFlows) {
            Object.keys(flowsState.legacyFlows).forEach((flowId: string) => {
                flows[flowId] = flowsState.legacyFlows[flowId];
            });
        }

        // Future: Export visual flows if they exist
        // if (flowsState && flowsState.nodes && flowsState.nodes.length > 0) {
        //     // Convert visual flow nodes to old format
        //     const convertedFlow = flowsState.nodes.map(node => ({
        //         ...node.processor,
        //         arguments: node.arguments,
        //         saveOutput: node.saveOutput,
        //     }));
        //     flows['visual-flow'] = {
        //         id: 1,
        //         galleryId: activationId,
        //         enabled: true,
        //         environmentId: 1,
        //         flowDefinition: JSON.stringify(convertedFlow),
        //     };
        // }

        const sharing =
            localExperience && localExperience.sharing
                ? localExperience.sharing.map((sharing: Sharing) => {
                      // Convert screen IDs to full screen objects
                      const screens = sharing.screens
                          .map((screenId: string) => localScreens[screenId])
                          .filter(
                              (screen) =>
                                  screen !== null && screen !== undefined,
                          );

                      return {
                          name: sharing.name,
                          screens: screens.map(transformScreenForExport),
                      };
                  })
                : [];

        return {
            ...localExperience,
            screens,
            processingFlows: flows,
            exitPin: localExperience.exitPin,
            mirror: localExperience.mirror,
            sharing,
        };
    };

    const createSharingStation = (
        name: string,
        screens: ScreenDefinition[] = [],
    ) => {
        const sharing = {
            name,
            screens,
        };
        dispatch(
            addSharingStation({
                experienceId: activationId,
                sharing,
            }),
        );
    };

    const deleteSharingStation = (sharingName: string) => {
        dispatch(
            removeSharingStation({
                experienceId: activationId,
                sharingName,
            }),
        );
    };

    const updateSharingStationName = (oldName: string, newName: string) => {
        const existingSharing = sharing.find((s: any) => s.name === oldName);
        if (existingSharing) {
            dispatch(
                updateSharingStation({
                    experienceId: activationId,
                    oldName,
                    newSharing: { ...existingSharing, name: newName },
                }),
            );
        }
    };

    const addScreenToSharingStation = (
        sharingName: string,
        screen: ScreenDefinition,
    ) => {
        const existingSharing = sharing.find(
            (s: any) => s.name === sharingName,
        );
        if (existingSharing) {
            // Add the screen to the screens Redux store first
            dispatch(addScreen(screen));

            // Add the screen ID (not the full object) to the sharing station
            const updatedScreens = [...existingSharing.screens, screen.id];
            dispatch(
                updateSharingStation({
                    experienceId: activationId,
                    oldName: sharingName,
                    newSharing: { ...existingSharing, screens: updatedScreens },
                }),
            );
        }
    };

    const removeScreenFromSharingStation = (
        sharingName: string,
        screenId: string,
    ) => {
        const existingSharing = sharing.find(
            (s: any) => s.name === sharingName,
        );
        if (existingSharing) {
            // Filter out the screen ID from the screens array
            const updatedScreens = existingSharing.screens.filter(
                (id: string) => id !== screenId,
            );
            dispatch(
                updateSharingStation({
                    experienceId: activationId,
                    oldName: sharingName,
                    newSharing: { ...existingSharing, screens: updatedScreens },
                }),
            );
        }
    };

    const deleteScreenFromSharingStation = (
        sharingName: string,
        screenId: string,
    ) => {
        removeScreenFromSharingStation(sharingName, screenId);
    };

    const updateSharingStationData = (
        sharingName: string,
        newSharingData: any,
    ) => {
        dispatch(
            updateSharingStation({
                experienceId: activationId,
                oldName: sharingName,
                newSharing: newSharingData,
            }),
        );
    };

    useEffect(() => {
        dispatch(resetExperience());
        dispatch(resetScreens());
        dispatch(clearAllFlows());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        loadExperience,
        screens, // Main experience screens only
        allScreens, // All screens (including sharing station screens)
        sharing,
        createSharingStation,
        deleteSharingStation,
        updateSharingStationName,
        addScreenToSharingStation,
        removeScreenFromSharingStation,
        deleteScreenFromSharingStation,
        updateSharingStationData,
        mirrorEnabled,
        setMirrorEnabled,
        handleChange,
        handleSwitchChange,
        handleSelectChange,
        exportExperience,
    };
}
